import { Fragment, h } from "preact";
import { useState, useEffect } from "preact/hooks";

import useSearch from "../helpers/search/useSearch";
import useDebounce from "../helpers/useDebounce";
import { SearchIcon } from "../helpers/icons.jsx";
import SearchPageResults from "./searchPageResults";
import SearchArticlesResults from "./searchArticleResults";
import SearchTabs from "./searchTabs";
import SearchProductResults from "./searchProductResults";
import { TABS } from "./constants";

const SearchContent = ({ data }) => {
  const { searchTerm, updateUrl, searchRoute, translations } = data;

  const [count, setCount] = useState(null);
  const [productCount, setProductCount] = useState(0);
  const [hasNoResults, setHasNoResults] = useState(true);
  const [terms, setTerms] = useState(searchTerm || "");
  const [productsLoading, setProductsLoading] = useState(false);
  const [searchLoading, searchData] = useSearch({ searchRoute, terms });
  const loading = useDebounce(searchLoading || productsLoading, 50);
  const [activeTab, setActiveTab] = useState(TABS.products);

  const onTermInput = (e) => setTerms(e.target.value);

  useEffect(() => {
    searchData &&
      setCount(
        productCount + searchData.pages.length + searchData.articles.length,
      );
  }, [productCount, searchData]);

  useEffect(() => {
    if (searchData?.searchTerm === "") {
      setCount(null);
    } else if (searchData?.ids.length === 0 && searchData?.pages.length === 0) {
      setCount(0);
    }

    if (searchData) {
      if (
        !searchData.ids.length &&
        !searchData.pages.length &&
        searchData.articles.length
      ) {
        setActiveTab(TABS.articles);
      } else if (!searchData.ids.length && searchData.pages.length) {
        setActiveTab(TABS.pages);
      } else if (searchData.ids.length && !searchData.pages.length) {
        setActiveTab(TABS.products);
      }
    }
  }, [searchData]);

  // Update page URL
  useEffect(() => {
    if (Shopify.designMode || !updateUrl || !searchData) return;

    const url = new URL(window.location);
    url.searchParams.set("q", searchData.searchTerm);
    window.history.pushState({}, "", url);
  }, [searchData]);

  const searchStatus = () => {
    if (terms === "") {
      setHasNoResults(true);
      return null;
    } else if (loading || productsLoading) {
      setHasNoResults(false);
      return translations.loading;
    } else if (count === 0) {
      setHasNoResults(true);
      return translations.no_results;
    } else if (count > 0) {
      setHasNoResults(false);
      return (
        <SearchTabs
          productCount={productCount}
          pageCount={searchData.pages.length}
          articleCount={searchData.articles.length}
          translations={translations}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
      );
    }
    return <br />;
  };

  return (
    <Fragment>
      <form
        action={searchRoute}
        method="get"
        className="main-search__input-wrapper"
      >
        <div className="main-search__query-input-container input-container">
          {SearchIcon()}
          <input
            className="main-search__query-input form_input"
            name="q"
            placeholder={translations.placeholder}
            type="text"
            value={terms}
            onInput={onTermInput}
          />
        </div>
      </form>

      {terms !== "" && (
        <div className="main-search__status text text-large">
          {searchStatus()}
        </div>
      )}
      {!hasNoResults && (
        <>
          <SearchProductResults
            ids={searchData?.ids}
            setProductCount={setProductCount}
            setProductsLoading={setProductsLoading}
            loading={loading}
            activeTab={activeTab}
            translations={translations}
          />
          <SearchPageResults
            pages={searchData?.pages}
            activeTab={activeTab}
            translations={translations}
          />
          <SearchArticlesResults
            articles={searchData?.articles}
            activeTab={activeTab}
            translations={translations}
          />
        </>
      )}
    </Fragment>
  );
};

export default SearchContent;
